import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import {
  AssignFormPayload,
  ClientForm,
  ClientFormDetailResponse,
  ClientFormSummary,
  FormCategory,
  SaveDraftResponse,
  SendFormLinkResponse,
  SubmitFormResponse,
} from "../types/formTemplates";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useSalon } from "./SalonContext";
import { useApp } from "./AppContext";

interface ListFilters {
  category?: FormCategory;
}

interface ClientFormsContextType {
  isLoading: boolean;
  error: string | null;
  listClientForms: (
    clientId: string,
    filters?: ListFilters,
  ) => Promise<ClientFormSummary[]>;
  getClientForm: (
    clientId: string,
    formId: string,
  ) => Promise<ClientFormDetailResponse>;
  assignForm: (
    clientId: string,
    payload: AssignFormPayload,
  ) => Promise<ClientForm>;
  saveDraft: (
    clientId: string,
    formId: string,
    data: Record<string, unknown>,
  ) => Promise<SaveDraftResponse>;
  submitForm: (
    clientId: string,
    formId: string,
    data?: Record<string, unknown>,
  ) => Promise<SubmitFormResponse>;
  deleteClientForm: (clientId: string, formId: string) => Promise<void>;
  expireClientForm: (clientId: string, formId: string) => Promise<ClientForm>;
  markFormOpened: (
    clientId: string,
    formId: string,
  ) => Promise<{ is_opened: boolean; flipped: boolean }>;
  renameClientForm: (
    clientId: string,
    formId: string,
    customName: string | null,
  ) => Promise<ClientForm>;
  sendFormLink: (transactionUuid: string) => Promise<SendFormLinkResponse>;
}

const ClientFormsContext = createContext<ClientFormsContextType | null>(null);

function extractMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "An unexpected error occurred";
}

function isClientFormLike(value: unknown): value is ClientForm {
  if (!value || typeof value !== "object") return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    "data" in o &&
    (o.data === null || typeof o.data === "object")
  );
}

/**
 * PUT save-draft and POST submit may return either `{ form, changes? }` or a
 * flat `ClientForm` at the root (compliance API). Normalize to `{ form, changes }`.
 */
function normalizeSubmitOrSaveDraftResponse(raw: unknown): {
  form: ClientForm;
  changes: number;
} {
  if (!raw || typeof raw !== "object") {
    throw new Error("Empty form response");
  }
  const rec = raw as Record<string, unknown>;
  if (rec.form && typeof rec.form === "object" && isClientFormLike(rec.form)) {
    return {
      form: rec.form as ClientForm,
      changes: typeof rec.changes === "number" ? rec.changes : 0,
    };
  }
  if (isClientFormLike(raw)) {
    return { form: raw as ClientForm, changes: 0 };
  }
  throw new Error("Unrecognized form write response");
}

export function ClientFormsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { businessInfo } = useSalon() as any;
  const { fetchWithAuth } = useApp();

  const requireSalon = useCallback((): string => {
    const id = businessInfo?.id;
    if (!id) throw new Error("No salon ID available");
    return id;
  }, [businessInfo?.id]);

  const listClientForms = useCallback(
    async (
      clientId: string,
      filters?: ListFilters,
    ): Promise<ClientFormSummary[]> => {
      const salonId = requireSalon();
      try {
        setIsLoading(true);
        setError(null);
        const query = filters?.category ? `category=${filters.category}` : "";
        const response: ClientFormSummary[] = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORMS, query, {
            salonId,
            clientId,
          }),
        );
        return response ?? [];
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const getClientForm = useCallback(
    async (
      clientId: string,
      formId: string,
    ): Promise<ClientFormDetailResponse> => {
      const salonId = requireSalon();
      try {
        setError(null);
        const response: ClientFormDetailResponse = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_BY_ID, "", {
            salonId,
            clientId,
            formId,
          }),
        );
        return response;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const assignForm = useCallback(
    async (
      clientId: string,
      payload: AssignFormPayload,
    ): Promise<ClientForm> => {
      const salonId = requireSalon();
      try {
        setIsLoading(true);
        setError(null);
        const response: ClientForm = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORMS, "", {
            salonId,
            clientId,
          }),
          { method: "POST", body: JSON.stringify(payload) },
        );
        return response;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const saveDraft = useCallback(
    async (
      clientId: string,
      formId: string,
      data: Record<string, unknown>,
    ): Promise<SaveDraftResponse> => {
      const salonId = requireSalon();
      try {
        setError(null);
        const raw = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_BY_ID, "", {
            salonId,
            clientId,
            formId,
          }),
          { method: "PUT", body: JSON.stringify({ data }) },
        );
        const { form, changes } = normalizeSubmitOrSaveDraftResponse(raw);
        return { form, changes } satisfies SaveDraftResponse;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const submitForm = useCallback(
    async (
      clientId: string,
      formId: string,
      data?: Record<string, unknown>,
    ): Promise<SubmitFormResponse> => {
      const salonId = requireSalon();
      try {
        setIsLoading(true);
        setError(null);
        const body = data !== undefined ? { data } : {};
        const raw = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_SUBMIT, "", {
            salonId,
            clientId,
            formId,
          }),
          { method: "POST", body: JSON.stringify(body) },
        );
        const { form } = normalizeSubmitOrSaveDraftResponse(raw);
        return { form } satisfies SubmitFormResponse;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const deleteClientForm = useCallback(
    async (clientId: string, formId: string): Promise<void> => {
      const salonId = requireSalon();
      try {
        setError(null);
        await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_BY_ID, "", {
            salonId,
            clientId,
            formId,
          }),
          { method: "DELETE" },
        );
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const expireClientForm = useCallback(
    async (clientId: string, formId: string): Promise<ClientForm> => {
      const salonId = requireSalon();
      try {
        setError(null);
        const response: ClientForm = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_EXPIRE, "", {
            salonId,
            clientId,
            formId,
          }),
          { method: "POST", body: JSON.stringify({}) },
        );
        return response;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      }
    },
    [requireSalon, fetchWithAuth],
  );

  /**
   * Fire-and-forget marker that the staff viewer has opened this form for the
   * first time. Backend is idempotent (no-op if already opened) and writes a
   * single ``form_opened`` audit row only on the call that actually flips.
   * Callers should not await this — failures are non-blocking.
   */
  const markFormOpened = useCallback(
    async (
      clientId: string,
      formId: string,
    ): Promise<{ is_opened: boolean; flipped: boolean }> => {
      const salonId = requireSalon();
      // Intentionally no setError on failure — fire-and-forget callers don't
      // surface this to the user, and we don't want to pollute context state.
      return await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_MARK_OPENED, "", {
          salonId,
          clientId,
          formId,
        }),
        { method: "POST", body: JSON.stringify({}) },
      );
    },
    [requireSalon, fetchWithAuth],
  );

  const renameClientForm = useCallback(
    async (
      clientId: string,
      formId: string,
      customName: string | null,
    ): Promise<ClientForm> => {
      const salonId = requireSalon();
      try {
        setError(null);
        const response: ClientForm = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_RENAME, "", {
            salonId,
            clientId,
            formId,
          }),
          {
            method: "POST",
            body: JSON.stringify({ custom_name: customName }),
          },
        );
        return response;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      }
    },
    [requireSalon, fetchWithAuth],
  );

  const sendFormLink = useCallback(
    async (transactionUuid: string): Promise<SendFormLinkResponse> => {
      try {
        setError(null);
        const response: SendFormLinkResponse = await fetchWithAuth(
          getApiUrl(API_CONFIG.ENDPOINTS.SEND_FORM_LINK, "", {
            transactionUuid,
          }),
          { method: "POST", body: JSON.stringify({}) },
        );
        return response;
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      }
    },
    [fetchWithAuth],
  );

  // [LOOP] churn probe
  useValueChurnProbe("ClientFormsContext", { isLoading, error });

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd, so the deps are all stable references.
  const value: ClientFormsContextType = useMemo(
    () => ({
      isLoading,
      error,
      listClientForms,
      getClientForm,
      assignForm,
      saveDraft,
      submitForm,
      deleteClientForm,
      expireClientForm,
      markFormOpened,
      renameClientForm,
      sendFormLink,
    }),
    [
      isLoading,
      error,
      listClientForms,
      getClientForm,
      assignForm,
      saveDraft,
      submitForm,
      deleteClientForm,
      expireClientForm,
      markFormOpened,
      renameClientForm,
      sendFormLink,
    ],
  );

  return (
    <ClientFormsContext.Provider value={value}>
      {children}
    </ClientFormsContext.Provider>
  );
}

export function useClientForms() {
  const ctx = useContext(ClientFormsContext);
  if (!ctx) {
    throw new Error("useClientForms must be used within a ClientFormsProvider");
  }
  return ctx;
}
